import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { isSupportedSetupProductType } from '../constants/setup-product-types';
import { useSetupStore } from '../store/setup.store';

/**
 * Main hook for orchestrating the setup flow.
 * Handles initialization and step configuration.
 * Calculates initial type from URL during render (React best practice).
 */
export function useSetupFlow() {
  const [searchParams] = useSearchParams();
  const type = useSetupStore((state) => state.type);
  const setType = useSetupStore((state) => state.setType);
  const goToStep = useSetupStore((state) => state.goToStep);

  // Calculate initial type from URL during render (not in Effect - React best practice)
  // No caching needed - this is a pure computation from searchParams
  const initialTypeFromUrl = useMemo(() => {
    const urlType = searchParams.get('t');
    return isSupportedSetupProductType(urlType) ? urlType : undefined;
  }, [searchParams]);

  // Set type in store if URL has type and store doesn't have it yet
  // Only set on initial load, not continuously (to allow user to switch types)
  const hasInitializedTypeRef = useRef(false);
  useEffect(() => {
    if (initialTypeFromUrl && !type && !hasInitializedTypeRef.current) {
      setType(initialTypeFromUrl);
      hasInitializedTypeRef.current = true;
    }
  }, [initialTypeFromUrl, type, setType]);

  // Calculate current type during render
  const currentType = type || initialTypeFromUrl;

  /**
   * Initialize setup flow by setting the first step.
   * Call this after hash is validated.
   * Memoized to ensure stability when used as a dependency.
   */
  const initialize = useCallback(() => {
    goToStep(0);
  }, [goToStep]);

  return {
    currentType,
    initialType: initialTypeFromUrl,
    initialize,
  };
}
